import { initModal, ModalClasses } from '@superkoders/modal';
import { Controller } from '@hotwired/stimulus';
import { iconSvg } from '../tools/iconSvg';

const fetchLoader = async (url, element) =>
	await fetch(url, {
		headers: {
			'X-Requested-With': 'XMLHttpRequest',
		},
	}).then((response) => {
		const contentType = response.headers.get('Content-Type');
		if (contentType.includes('application/json')) {
			return response.json().then((data) => {
				if (element && element.dataset.snippetid && data.snippets && data.snippets[element.dataset.snippetid]) {
					return `<div id="${element.dataset.snippetid}">${data.snippets[element.dataset.snippetid]}</div>`;
				} else {
					return 'Error: No valid snippet id provided.';
				}
			});
		} else {
			return response.text();
		}
	});

const baseModalSelector = '[data-modal]:not([data-modal-gallery]):not([data-modal-gallery-mobile])';

export const defaultOptions = {
	closeOnBgClick: true,
	removeOnClose: true,
	closeIcon: iconSvg('close', '', 'Close'),
	prevIcon: iconSvg('arrow-left', '', 'Previous slide'),
	nextIcon: iconSvg('arrow-right', '', 'Next slide'),
	modalSelector: `${baseModalSelector}:not([data-modal-no-bg-close])`,
	loaderTpl: function () {
		return '<div class="body-loader__loader"></div>';
	},
	navTpl: (items) =>
		items
			.map(
				(_item, index) =>
					`<span class="b-modal__nav-item ${ModalClasses.NAV_TRIGGER}" data-modal-index="${index}"><span class="sr-only">Slide ${index}</span></span>`,
			)
			.join(''),
	fetchLoader,
	focusTrapOptions: {
		allowOutsideClick: true,
		initialFocus: () => document.querySelector('.b-modal[open], .b-modal.is-opened'),
	},
	onModalOpen: () => {
		setTimeout(() => {
			const modalElement = document.querySelector('.b-modal[open], .b-modal.is-opened');
			if (modalElement) {
				const hasChoices = modalElement.querySelector('.choices');
				if (hasChoices) {
					modalElement.classList.add('b-modal--choices');
				}
			}
		}, 0);
	},
	onModalContentLoaded: () => {
		const modalElement = document.querySelector('.b-modal[open], .b-modal.is-opened');
		if (modalElement) {
			const firstInput = modalElement.querySelector('.inp-count__inp');
			if (firstInput) {
				setTimeout(() => {
					firstInput.focus();
				}, 150);
			}
			const hasChoices = modalElement.querySelector('.choices');
			if (hasChoices) {
				modalElement.classList.add('b-modal--choices');
			}
		}
	},
};

export const defaultOptionsNoBgClick = {
	...defaultOptions,
	closeOnBgClick: false,
	modalSelector: `${baseModalSelector}[data-modal-no-bg-close]`,
};

const wrappedModalIds = new Set();
const CLOSE_ACTION_SELECTOR = '[data-modal-close-action]';

export const wrapModalCloseAction = (modal) => {
	if (wrappedModalIds.has(modal.id)) {
		return;
	}
	wrappedModalIds.add(modal.id);

	const dialog = modal.element;
	const closeSelector = modal.options.closeSelector || '.js-modal-close';

	const tryCloseAction = () => {
		const actionBtn = dialog.querySelector(CLOSE_ACTION_SELECTOR);
		if (actionBtn) {
			actionBtn.click();
			return true;
		}
		return false;
	};

	const onCloseAttempt = (e) => {
		if (!tryCloseAction()) {
			return;
		}
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();
	};

	dialog.addEventListener(
		'click',
		(e) => {
			const isCloseButton = e.target.closest(closeSelector);
			const isActionButton = e.target.closest(CLOSE_ACTION_SELECTOR);
			const isBackdrop = e.target === dialog;

			if (isBackdrop || (isCloseButton && !isActionButton)) {
				onCloseAttempt(e);
			}
		},
		true,
	);

	// Escape
	dialog.addEventListener(
		'keydown',
		(e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				onCloseAttempt(e);
			}
		},
		true,
	);

	dialog.addEventListener(
		'close',
		(e) => {
			const targetDialog = e.target;
			if (targetDialog !== dialog) return;
			const actionBtn = targetDialog.querySelector(CLOSE_ACTION_SELECTOR);
			if (actionBtn) {
				actionBtn.click();
			}
		},
		true,
	);
};

export const defaultInit = (options) => {
	const instances = initModal(options);
	instances.forEach(wrapModalCloseAction);
};

export default class Modal extends Controller {
	connect() {
		defaultInit(defaultOptions);
		defaultInit(defaultOptionsNoBgClick);
	}
}
